import { ThemeOptions } from '@material-ui/core/styles/createTheme';
import { getTooltipStyles } from '../../atoms/Tooltip/Tooltip';
import { TinkTheme } from './theme';

export const overrideMuiTheme = (theme: TinkTheme): ThemeOptions => ({
  overrides: {
    MuiTooltip: {
      tooltip: getTooltipStyles(theme),
      // TODO: why is this needed?
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      arrow: {
        color: theme.palette.white.main,
        fontSize: theme.sizes.xxsmall,
      },
    },
    MuiButton: {
      contained: {
        backgroundColor: theme.palette.white.main,
        boxShadow: theme.shadows.medium,
        color: theme.palette.primary.main,
        '&:hover': {
          backgroundColor: theme.palette.white.main,
          boxShadow: theme.shadows.hover,
          color: theme.palette.primary[800],
        },
        '&:active': {
          transform: 'scale(0.96)',
        },
      },
      containedPrimary: {
        color: theme.palette.white.main,
        '&:hover': {
          color: theme.palette.white.main,
        },
      },
      containedSecondary: {
        color: theme.palette.white.main,
        '&:hover': {
          color: theme.palette.white.main,
          backgroundColor: theme.palette.secondary[800],
        },
      },
      fullWidth: {
        '&:active': {
          transform: 'none',
        },
      },
      outlined: {
        // TODO: Does the padding have to be set explicitly for each button type?
        padding: `${theme.sizes.xsmall} ${theme.sizes.medium}`,
        '&:active': {
          transform: 'scale(0.96)',
        },
      },
      root: {
        borderRadius: theme.sizes.medium,
        color: theme.palette.primary[600],
        fontFamily: theme.font.face,
        fontSize: theme.sizes.small,
        fontWeight: theme.font.weights.semiBold,
        lineHeight: 1,
        padding: `${theme.sizes.xsmall} ${theme.sizes.medium}`,
        textTransform: 'none',
        transition: 'all .15s cubic-bezier(0.4, 0, 0.2, 1)',
      },
      sizeLarge: {
        fontSize: theme.sizes.normal,
        padding: `${theme.sizes.normal} ${theme.sizes.large}`,
      },
      sizeSmall: {
        fontSize: theme.sizes.small,
        padding: `${theme.sizes.xxsmall} ${theme.sizes.normal}`,
      },
      textPrimary: {
        '&:hover': {
          backgroundColor: 'transparent',
          color: theme.palette.primary[800],
        },
      },
      textSecondary: {
        '&:hover': {
          backgroundColor: 'transparent',
          color: theme.palette.secondary[900],
        },
      },
      text: {
        // TODO: Does the padding have to be set explicitly for each button type?
        padding: `${theme.sizes.xsmall} ${theme.sizes.medium}`,
        '&:hover': {
          backgroundColor: 'transparent',
          color: theme.palette.primary[800],
        },
      },
      startIcon: {
        marginRight: theme.sizes.xsmall,
      },
    },
    MuiCard: {
      root: {
        borderRadius: `${theme.roundedCorners.small}`,
        boxShadow: `${theme.shadows.small}`,
      },
    },
    MuiTextField: {
      root: {
        marginTop: '0.375rem',
        marginBottom: theme.sizes.small,
      },
    },
    MuiInput: {
      root: {
        '&$error': {
          color: theme.palette.primary.main,
        },
      },
      underline: {
        '&:after': {
          borderBottom: `1px solid ${theme.palette.primary.main}`,
        },
        '&:before': {
          borderBottom: `1px solid ${theme.palette.greyBorder.main}`,
        },
        '&:hover:not($disabled):before': {
          borderBottom: `1px solid ${theme.palette.primary.main}`,
        },
      },
    },
    MuiInputLabel: {
      root: {
        color: `${theme.palette.greyLight.main}`,
        '&$error': {
          color: `${theme.palette.greyLight.main}`,
        },
      },
      outlined: {
        position: 'relative',
        color: theme.palette.greyBlack.main,
        fontSize: theme.sizes.small,
        transform: 'none !important',
        alignSelf: 'flex-start',
        pointerEvents: 'auto',
        '&$focused': {
          color: theme.palette.greyBlack.main,
        },
      },
    },
    MuiOutlinedInput: {
      root: {
        position: 'relative',
        marginTop: theme.sizes.xxsmall,
        fontSize: theme.sizes.normal,
        boxShadow: `0px 1px 4px 0 ${theme.palette.metal[600]}`,
        outline: 'none',
        '&:hover, &$focused': {
          boxShadow: `0px 2px 8px 0 ${theme.palette.metal[600]}`,
        },
      },
      input: {
        height: '1.25em',
        padding: `${theme.sizes.small} ${theme.sizes.normal}`,
        borderRadius: theme.roundedCorners.xsmall,
        transition: 'box-shadow 0.2s ease-in-out',
        '&::placeholder': {
          color: theme.palette.metal[700],
          opacity: 1,
        },
      },
      inputMultiline: {
        height: 'auto',
      },
      multiline: {
        padding: theme.sizes.small,
      },
      notchedOutline: {
        top: 0,
        borderColor: 'transparent',
        '&:hover': {
          borderColor: 'transparent',
        },
        '& legend': {
          display: 'none',
        },
        '.MuiOutlinedInput-root:hover &': {
          borderColor: 'transparent',
        },
        '.MuiOutlinedInput-root.Mui-focused &': {
          borderColor: theme.palette.primary.main,
          borderWidth: '1px',
        },
      },
    },
    MuiListItem: {
      root: {
        '&$selected': {
          color: theme.palette.primary.main,
        },
        fontFamily: theme.font.face,
        paddingLeft: '0',
      },
    },
    MuiListItemIcon: {
      root: {
        color: 'inherit',
        marginRight: '1rem',
        minWidth: 'auto',
      },
    },
    MuiMenu: {
      paper: {
        boxShadow: `${theme.shadows.medium}`,
      },
    },
    MuiSelect: {
      selectMenu: {
        '&&': {
          height: '1.25em',
          paddingRight: `${theme.sizes.xlarge}`,
        },
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
      icon: {
        color: `${theme.palette.primary.main} !important`,
      },
    },
    MuiSwitch: {
      root: {
        width: 51,
        height: 31,
        padding: 0,
        margin: theme.sizes.xxsmall,
      },
      colorPrimary: {
        color: theme.palette.primary[400],
      },
      colorSecondary: {
        color: theme.palette.secondary[500],
      },
      switchBase: {
        padding: 4,
        '&$checked': {
          transform: 'translateX(19px)',
        },
        '&:hover': {
          backgroundColor: 'transparent !important',
        },
      },
      thumb: {
        width: 23,
        height: 23,
        marginLeft: 1,
        boxShadow: 'none',
        '$checked &': {
          color: theme.palette.white.main,
        },
      },
      track: {
        borderRadius: 16,
        transition: 'background .15s ease-out',
        '$colorPrimary + &': {
          backgroundColor: theme.palette.primary[300],
        },
        '$colorSecondary + &': {
          backgroundColor: theme.palette.secondary[400],
        },
        '$colorPrimary$disabled + &': {
          backgroundColor: theme.palette.greyLight.main,
          cursor: 'no-drop',
        },
        opacity: 1,
        '$switchBase$checked + &': {
          opacity: 1,
        },
      },
    },
    MuiTableCell: {
      body: {
        fontSize: theme.sizes.normal,
        lineHeight: theme.sizes.medium,
        borderBottom: `1px solid ${theme.palette.greySoft.main}`,
      },
      head: {
        color: theme.palette.greyBlack.main,
        fontSize: theme.sizes.small,
        fontWeight: theme.font.weights.semiBold,
        lineHeight: 'inherit',
        letterSpacing: '1px',
        paddingBottom: theme.sizes.xxsmall,
        paddingTop: theme.sizes.xxsmall,
      },
      stickyHeader: {
        backgroundColor: 'inherit',
      },
      root: {
        // Can't override mui's padding with padding-bottom etc.
        padding: `${theme.sizes.small} ${theme.sizes.large} ${theme.sizes.small} ${theme.sizes.large}`,
      },
    },
    MuiTableRow: {
      root: {
        backgroundColor: 'inherit',
      },
      head: {
        height: theme.sizes.large,
      },
      hover: {
        '$root&:hover': {
          backgroundColor: theme.palette.primary[100],
        },
      },
    },
    MuiTab: {
      root: {
        '&$selected': {
          fontWeight: 'bold',
        },
      },
      wrapper: {
        fontSize: theme.sizes.small,
      },
    },
    MuiFormControlLabel: {
      root: {
        marginLeft: 0,
        padding: '4px 0 4px 0',
      },
      label: {
        fontSize: theme.sizes.small,
      },
    },
    MuiFormHelperText: {
      contained: {
        marginLeft: 0,
        fontSize: theme.sizes.small,
      },
    },
    MuiExpansionPanel: {
      root: {
        border: `1px solid ${theme.palette.primary['200']}`,
        '&$expanded': {
          margin: 0,
        },
        '&:before': {
          display: 'none',
        },
      },
    },
    MuiExpansionPanelSummary: {
      root: {
        paddingTop: theme.sizes.xsmall,
        paddingRight: theme.sizes.normedium,
        paddingBottom: theme.sizes.small,
        paddingLeft: theme.sizes.normedium,
      },
      content: {
        margin: 0,
        '&$expanded': {
          margin: 0,
        },
      },
      expandIcon: {
        order: -1,
        '&$expanded': {
          transform: 'rotate(90deg)',
        },
      },
    },
    MuiExpansionPanelDetails: {
      root: {
        paddingTop: 0,
        paddingBottom: theme.sizes.normedium,
      },
    },
  },
  palette: {
    contrastThreshold: 3, // Maximize the contrast between the background and the text.
    error: theme.palette.error,
    primary: theme.palette.primary,
    secondary: theme.palette.secondary,
  },
  typography: {
    fontFamily: theme.font.face,
  },
});
